import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { Shield, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

export const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services`);
        const data = await response.json();
        setServices(data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  if (loading) {
    return (
      <section id="services" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">Loading services...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - Enhanced */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wide">Our Services</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Comprehensive Pet Relocation Services
          </h2>
          <p className="text-base sm:text-lg text-foreground/70 leading-relaxed">
            Cayman Islands-based expertise for pet relocation to and from the Islands, new pet imports, and vacation travel assistance
          </p>
        </div>

        {/* Services Grid - Enhanced */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = Icons[service.icon];
            // Assign color themes to services - aligned with site color scheme
            const colorThemes = [
              { 
                gradient: 'from-teal-500/10 to-cyan-500/10', 
                iconBg: 'bg-gradient-to-br from-teal-500 to-cyan-600', 
                iconHover: 'group-hover:scale-110',
                border: 'border-teal-500/20',
                hover: 'hover:border-teal-500/40'
              },
              { 
                gradient: 'from-blue-500/10 to-indigo-500/10', 
                iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600', 
                iconHover: 'group-hover:scale-110',
                border: 'border-blue-500/20',
                hover: 'hover:border-blue-500/40'
              },
              { 
                gradient: 'from-emerald-500/10 to-teal-500/10', 
                iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600', 
                iconHover: 'group-hover:scale-110',
                border: 'border-emerald-500/20',
                hover: 'hover:border-emerald-500/40'
              },
              { 
                gradient: 'from-cyan-500/10 to-blue-500/10', 
                iconBg: 'bg-gradient-to-br from-cyan-500 to-blue-600', 
                iconHover: 'group-hover:scale-110',
                border: 'border-cyan-500/20',
                hover: 'hover:border-cyan-500/40'
              },
              { 
                gradient: 'from-indigo-500/10 to-purple-500/10', 
                iconBg: 'bg-gradient-to-br from-indigo-500 to-purple-600', 
                iconHover: 'group-hover:scale-110',
                border: 'border-indigo-500/20',
                hover: 'hover:border-indigo-500/40'
              },
              { 
                gradient: 'from-sky-500/10 to-cyan-500/10', 
                iconBg: 'bg-gradient-to-br from-sky-500 to-cyan-600', 
                iconHover: 'group-hover:scale-110',
                border: 'border-sky-500/20',
                hover: 'hover:border-sky-500/40'
              },
            ];
            const theme = colorThemes[index % colorThemes.length];
            
            return (
              <Card 
                key={service.id} 
                className={`group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 ${theme.border} ${theme.hover} bg-white overflow-hidden relative`}
              >
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                
                <CardHeader className="relative z-10">
                  <div className={`w-16 h-16 rounded-xl ${theme.iconBg} ${theme.iconHover} flex items-center justify-center mb-4 group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground group-hover:text-foreground transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <CardDescription className="text-foreground/70 leading-relaxed group-hover:text-foreground/80 transition-colors">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA Image Section - Enhanced */}
        <div className="mt-20 relative rounded-2xl overflow-hidden shadow-2xl">
          <img
            src="https://images.unsplash.com/photo-1770836037816-4445dbd449fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBwZXQlMjBjYXJlfGVufDB8fHx8MTc3MzI3ODEzNHww&ixlib=rb-4.1.0&q=85"
            alt="Professional Pet Care"
            className="w-full h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/80 to-accent/90 flex items-center justify-center">
            <div className="text-center text-white px-6 max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/30">
                <Sparkles className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold uppercase tracking-wide">Get Started Today</span>
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                Ready to Start Your Pet's Journey?
              </h3>
              <p className="text-lg sm:text-xl mb-8 text-white/90 leading-relaxed">
                Get a personalized quote tailored to your pet's unique travel needs
              </p>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-primary hover:bg-white/90 px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-xl inline-flex items-center gap-2"
              >
                Contact Us Today
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
